// @ts-check
import {
  createCompetitiveBuildingCompContent,
  createCompetitiveBuildingContent,
  createCompetitiveMultiFamilyBuildingContent,
  createCompetitiveDataCenterContent,
  createProjectContent,
  createWMTSTileContent,
  createCompetitivePortfolioContent,
  createIndustrialBuildingContent,
  createCompetitiveOmContent,
  createBulkReviewContent,
} from '@/lib/classes/InfoWindowContent';
import {getDocument} from '@/services/FirestoreService';

/**
 * @typedef {Record<string, Array>} FieldSuggestions
 *
 * @typedef {Object} BaseIWState
 * @property {InfoWindowUpdatePayload[]} infoWindowContents
 * @property {string | null} featureName
 * @property {FieldSuggestions} fieldSuggestions
 * @property {boolean} openInEditMode
 * @property {string | null} selectedContentId
 * @property {number} selectedTabIndex
 *
 * @typedef {import('vuex').ActionContext<BaseIWState, any>} ActionContext
 *
 * @typedef {import('@/lib/classes/InfoWindowContent').InfoWindowContent} InfoWindowContent
 * @typedef {import('@/lib/classes/InfoWindowContent/helpers/wmtsTile').WMTSTileType} WMTSTileType
 *
 * @typedef {Object} InfoWindowUpdatePayload
 * @property {InfoWindowContent | Promise<InfoWindowContent>} content
 * @property {number} [insertIndex]
 * @property {boolean} [openInEditMode]
 * @property {string | symbol} [id]
 */

const validMarkerTypes = new Set([
  'industrialBuilding',
  'multiFamilyBuilding',
  'dataCenterBuilding',
  'competitiveBuilding',
  'competitiveBuildingComp',
  'competitivePortfolio',
  'industrialProject',
  'acquisitionProject',
  'dataCenterProjects',
  'multiFamilyProject',
  'multiFamilyAcquisitionProject',
]);

/**
 * Fetches the field suggestions from the database.
 * @param {string} configCollection The name of the collection to fetch the suggestions from.
 * @return {Promise<FieldSuggestions>} A `Promise` resolving to the field suggestions.
 */
const fetchFieldSuggestions = async (configCollection) => {
  try {
    const suggestions = await getDocument(configCollection, 'fieldSuggestions');
    if (typeof suggestions !== 'object') {
      throw new Error('Field suggestions are not in the correct format');
    }
    for (const key in suggestions) {
      if (!Array.isArray(suggestions[key])) {
        throw new Error('Field suggestions are not in the correct format');
      }
    }
    return suggestions;
  } catch (error) {
    error.message = `Could not load suggested fields: ${error.message}`;
    throw error;
  }
};

/**
 * Returns true if this InfoWindowContent has anything to actually display. Cannot check for overlays because they may
 * not be populated at any given moment.
 * @param {InfoWindowContent|undefined} content
 */
const hasVisibleContent = (content) => !!(
  content && content.id && (
    content.comps?.length ||
    content.displayFields?.length ||
    content.title ||
    content.toolbar?.toolbarOptions?.length
  ));

/**
 * @type {BaseIWState}
 */
const baseState = {
  infoWindowContents: [],
  featureName: null,
  fieldSuggestions: {},
  openInEditMode: false, // this is only used for placemarkers in shareable maps
  selectedContentId: null,
  selectedTabIndex: 0,
};
const baseMutations = {
  /**
   * @param {BaseIWState} state
   * @param {string} newName
   */
  updateFeatureName(state, newName) {
    state.featureName = newName;
  },
  /**
   * @param {BaseIWState} state
   * @param {FieldSuggestions} newSuggestions
   */
  updateFieldSuggestions(state, newSuggestions) {
    state.fieldSuggestions = newSuggestions;
  },
  /**
   * Push one or more tabs of InfoWindowContent to state.
   * @param {BaseIWState} state
   * @param {InfoWindowUpdatePayload[] | InfoWindowUpdatePayload} newContents
   */
  async updateInfoWindowContents(state, newContents) {
    if (!Array.isArray(newContents)) {
      newContents = [newContents];
    }

    // clicking overlapping overlays will cause multiple independent tabs to be created, so we need to dedupe
    await Promise.allSettled(newContents.map(async ({content, insertIndex = null, openInEditMode = false}) => {
      content = await content;
      if (!(hasVisibleContent(content))) return;
      let existingIndex = state.infoWindowContents.findIndex((tab) => tab.id === content.id);

      let existingProjectIndex = -1;
      if (content.metadata?.projectId) {
        const projectId = content.metadata.projectId;
        // Only add the tab if it doesn't already exist OR if it's a different type of overlay
        for (let i = 0; i < state.infoWindowContents.length; i++) {
          const tabContent = await state.infoWindowContents[i].content;
          if (tabContent.metadata?.projectId === projectId && tabContent.type === content.type) {
            existingProjectIndex = i;
            break;
          }
        }
        if (existingProjectIndex !== -1) return;
      }

      if (existingIndex > -1 && existingProjectIndex > -1) {
        console.warn('Both existingIndex and existingProjectIndex are non-negative', {existingIndex, existingProjectIndex});
      }

      // assuming that only one or the other will be non-negative, select the non-negative one
      existingIndex = Math.max(existingIndex, existingProjectIndex);

      if (existingIndex !== -1) {
        state.infoWindowContents.splice(existingIndex, 1, {content, openInEditMode, id: content.id});
        return;
      }

      if (insertIndex == null) {
        insertIndex = state.infoWindowContents.length;
      }
      state.infoWindowContents.splice(insertIndex, 0, {content, openInEditMode, id: content.id});

      /**
       * @param {InfoWindowUpdatePayload | null} payload
       * @returns {number}
       */
      const rankContent = (payload) => {
        const content = payload?.content;
        if (!content || content instanceof Promise) return 2; // Promise/null goes last
        if (validMarkerTypes.has(content.type)) return 0; // markers first
        return 1; // other resolved content in the middle
      };

      // Moving this sort outside of the Promise.allSettled() caused a visual bug in the tray where it appeared the
      // second tab was selected but still displayed the first tabs content. Keeping it inside as a result, but open to
      // refactoring in the future.
      state.infoWindowContents.sort((a, b) => {
        return rankContent(a) - rankContent(b);
      });
    }));
  },
  removeInfoWindowContent(state, index) {
    state.infoWindowContents.splice(index, 1);
  },
  /**
   * @param {BaseIWState} state
   */
  clearInfoWindowContents(state) {
    state.infoWindowContents = [];
  },
  /**
   * @param {BaseIWState} state
   * @param {string} id the info window content id
   */
  updateSelectedContentId(state, id) {
    state.selectedContentId = id;
  },
  /**
   * @param {BaseIWState} state
   * @param {number} index
   */
  setSelectedTabIndex(state, index) {
    state.selectedTabIndex = index;
  },
};
const baseActions = {
  // ==============================================================================================
  // Actions for creating an info window tab. Each action commits `updateInfoWindowContents`
  // ==============================================================================================
  /**
   * Creates an InfoWindow tab for a project.
   * @async Asynchronously fetches overlays for this project before opening the InfoWindow.
   * @param {Object} context Vuex context object.
   * @param {Object} payload Action payload.
   * @param {Object} payload.subject The project to open an InfoWindow for.
   * @param {number} payload.insertIndex The index to insert into InfoWindowContents
   */
  createProjectTab({commit}, {subject, insertIndex}) {
    commit('updateInfoWindowContents', {
      content: createProjectContent(subject),
      insertIndex,
    });
  },
  /**
   * Creates an InfoWindow tab for an industrial building.
   * @param {Object} context Vuex context object.
   * @param {Object} payload Action payload.
   * @param {Object} payload.subject The industrial building to open an InfoWindow for.
   * @param {number} payload.insertIndex The index to insert into InfoWindowContents
   */
  createIndustrialBuildingTab({commit}, {subject, insertIndex}) {
    commit('updateInfoWindowContents', {
      content: createIndustrialBuildingContent(subject),
      insertIndex,
    });
  },
  /**
   * Creates an InfoWindow tab for a competitive building.
   * @param {Object} context Vuex context object.
   * @param {Object} payload Action payload.
   * @param {Object} payload.subject The competitive building to open an InfoWindow for.
   * @param {number} payload.insertIndex The index to insert into InfoWindowContents
   */
  createCompetitiveBuildingTab({commit}, {subject, insertIndex}) {
    commit('updateInfoWindowContents', {
      content: createCompetitiveBuildingContent(subject),
      insertIndex,
    });
  },
  /**
   * Creates an InfoWindow tab for a competitive multi-family building.
   * @param {Object} context Vuex context object.
   * @param {Object} payload Action payload.
   * @param {Object} payload.subject The building to open an InfoWindow for.
   * @param {number} payload.insertIndex The index to insert into InfoWindowContents
   */
  createMultiFamilyBuildingTab({commit}, {subject, insertIndex}) {
    commit('updateInfoWindowContents', {
      content: createCompetitiveMultiFamilyBuildingContent(subject),
      insertIndex,
    });
  },
  /**
   * Creates an InfoWindow tab for a data center.
   * @param {Object} context Vuex context object.
   * @param {Object} payload Action payload.
   * @param {Object} payload.subject The data center to open an InfoWindow for.
   * @param {InfoWindowContent} payload.subject The info window content.
   * @param {number} payload.insertIndex The index to insert into InfoWindowContents
   */
  createDataCenterBuildingTab({commit}, {subject, insertIndex}) {
    commit('updateInfoWindowContents', {
      content: createCompetitiveDataCenterContent(subject),
      insertIndex,
    });
  },
  /**
   * Creates an InfoWindow tab for a comp.
   * @param {Object} context Vuex context object.
   * @param {Object} payload Action payload.
   * @param {Object} payload.subject The comp to open an InfoWindow for.
   * @param {number} payload.insertIndex The index to insert into InfoWindowContents
   */
  createCompTab({commit}, {subject, insertIndex}) {
    const parentData = subject?.parent;
    if (!parentData) throw new Error(`createCompTab: couldn't extract parent data from comp: ${subject}`);
    commit('updateInfoWindowContents', {
      content: createCompetitiveBuildingCompContent(
        {
          ...parentData,
          firestoreCollection: subject.firestoreCollection,
          filterCollection: subject.filterCollection,
          markerData: subject?.markerData,
        },
        subject.id,
      ),
      insertIndex,
    });
  },
  createCompetitiveOmTab({commit}, {subject, insertIndex}) {
    commit('updateInfoWindowContents', {
      content: createCompetitiveOmContent(subject),
      insertIndex,
    });
  },
  createBulkReviewTab({commit}, {subject, insertIndex}) {
    commit('updateInfoWindowContents', {
      content: createBulkReviewContent(subject),
      insertIndex,
    });
  },
  /**
   * Adds an InfoWindow tab for a WMTS tile.
   * @param {Object} context Vuex context object.
   * @param {Object} payload Action payload.
   * @param {Array<Object>} payload.subjects The WMTS tiles to open an InfoWindow for.
   * @param {WMTSTileType} payload.type The type of WMTS tile.
   */
  async createWMTSTileTab({commit}, {type, subjects}) {
    commit('updateInfoWindowContents', {
      content: await createWMTSTileContent(type, subjects),
    });
  },
  /**
   * Creates an InfoWindow tab for a competitive portfolio.
   * @param {Object} context Vuex context object.
   * @param {Object} payload Action payload.
   * @param {Object} payload.subject The competitive portfolio to open an InfoWindow for.
   * @param {number} payload.insertIndex The index to insert into InfoWindowContents
   */
  createCompetitivePortfolioTab({commit}, {subject, insertIndex}) {
    commit('updateInfoWindowContents', {
      content: createCompetitivePortfolioContent(subject),
      insertIndex,
    });
  },
  // ==============================================================================================
  // Miscellaneous info window actions.
  // ==============================================================================================
  /**
   * Closes an InfoWindow tab.
   * @param {ActionContext} context Vuex context object.
   * @param {string} id The ID of the tab to close
   * @return {number} The index of the removed tab, or -1 when no tab has that id.
   */
  closeInfoWindowTab({commit, state}, id) {
    const index = state.infoWindowContents.findIndex((content) => content.id === id);
    // splice(-1, 1) would close the last tab instead
    if (index !== -1) {
      commit('removeInfoWindowContent', index);
    }
    return index;
  },
  /**
   * Clears all InfoWindowContent, triggering the InfoWindow to close.
   * @param {ActionContext} context Vuex context object.
   * @return {void}
   */
  closeInfoWindow({commit}) {
    commit('clearInfoWindowContents');
  },
  /**
   * @param {ActionContext} context
   * @param {string} configCollection The name of the collection to fetch the suggestions from.
   */
  async loadFieldSuggestions({commit, dispatch}, configCollection) {
    try {
      const suggestions = await fetchFieldSuggestions(configCollection);
      commit('updateFieldSuggestions', suggestions);
    } catch (error) {
      console.error('Failed to load field suggestions', error);
      dispatch('addErrorMessage', 'Failed to load field suggestions', {root: true});
    }
  },
};

export {
  baseState,
  baseMutations,
  baseActions,
};
